import {
  format,
} from 'd3-format';
import { GetString } from 'fluent-react';
import React from 'react';
import styled, { css } from 'styled-components';
import {
  failIfValidOrNonExhaustive, growthProjectionsYear,
} from '../../Utils';
import { primaryColor1 } from '../Utils';
import Sparkline, {
  TimeSeriesDatum,
  Unit,
} from './Sparkline';

//#region Styling
const smallMonitorHeight = 750;

const Root = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;

  & + div {
    margin-top: 5vh;

    @media (min-height: ${smallMonitorHeight}px) {
      margin-top: 5rem;
    }
  }
`;
const textStyle = css`
  color: ${primaryColor1};
  text-shadow: 1px 1px 1px rgba(0, 0, 0, 0.1);
  text-transform: uppercase;
`;
const largeNumberStyle = css`
  font-size: 5vh;
  letter-spacing: 2px;

  @media (min-height: ${smallMonitorHeight}px) {
    font-size: 3rem;
  }
`;
const smallNumberStyle = css`
  font-size: 1.25rem;
`;
const IndicatorName = styled.div`
  ${textStyle}
  line-height: 1.25;
  margin-bottom: 0.4rem;
`;
const IndicatorRanking = styled.div`
  ${textStyle}
  font-size: 0.875rem;
`;
const GrowthProjectionsFigure = styled.div`
  ${textStyle}
  ${largeNumberStyle}
`;
const GDPPerCapitaNumber = styled.div`
  ${textStyle}
`;
const GDPPerCapitaFigure = styled.span`
  ${largeNumberStyle}
`;
const ECIRankContainer = styled.div`
  ${textStyle}
`;
const ECIRank = styled.span`
  ${largeNumberStyle}
`;
const ECIRankTotal = styled.span`
  ${smallNumberStyle}
`;

//#endregion

export const formatGDPPerCapita = format('$,');
// FIX: Change units of decimal precision from .3~% to .2~%
const formatGrowthProjection = format('.2~%');
const transitionDuration = 1000;

export enum Type {
  GDPPC,
  ECIRank,
  GrowthProjection,
}

export enum Status {
  Loading,
  Success,
  Failure,
}

type SuccessData = {
  type: Type.GDPPC,
  year: number
  gdpPerCapita: number
  rank: number
  rankTotalCount: number
  timeSeries: TimeSeriesDatum[],
} | {
  type: Type.ECIRank,
  rank: number
  rankTotalCount: number
  timeSeries: TimeSeriesDatum[],
} | {
  type: Type.GrowthProjection
  growth: number
  rank: number
  rankTotalCount: number,
};

type Props = {
  status: Status.Loading,
} | {
  status: Status.Failure,
} | ({
  status: Status.Success
  getFluentString: GetString;
  size: {width: number, height: number} | undefined
} & SuccessData);

const Widget = (props: Props) => {
  let content: React.ReactElement<any>;
  if (props.status === Status.Success) {
    const {getFluentString, size} = props;
    if (size === undefined) {
      content = (<div/>);
    } else {
      if (props.type === Type.GDPPC) {
        const {width, height} = size;
        const {year, gdpPerCapita, rank, rankTotalCount, timeSeries} = props;
        const title = getFluentString('landing-widget-gdp-per-capita-title', {
          year: year.toString(),
        });
        const rankText = getFluentString('landing-widget-gdp-per-capita-rank', {
          rank,
          total: rankTotalCount,
        });

        content = (
          <>
            <IndicatorName>{title}</IndicatorName>
            <GDPPerCapitaNumber>
              <GDPPerCapitaFigure>{formatGDPPerCapita(gdpPerCapita)}</GDPPerCapitaFigure>
            </GDPPerCapitaNumber>
            <IndicatorRanking dangerouslySetInnerHTML={{__html: rankText}}/>
            <Sparkline
              timeSeries={timeSeries}
              width={width}
              height={height}
              transitionDuration={transitionDuration}
              unit={Unit.dollars}
              getFluentString={getFluentString}
            />
          </>
        );

      } else if (props.type === Type.ECIRank) {
        const {width, height} = size;
        const {rank, rankTotalCount, timeSeries} = props;
        const title = getFluentString('landing-widget-eci-rank-title');
        const rankTotal = getFluentString('landing-widget-eci-rank-total', {
          rank,
          total: rankTotalCount,
        });
        content = (
          <>
            <IndicatorName>{title}</IndicatorName>
            <ECIRankContainer>
              <ECIRank>{rank}</ECIRank>
              <ECIRankTotal dangerouslySetInnerHTML={{__html: rankTotal}}/>
            </ECIRankContainer>
            <Sparkline
              timeSeries={timeSeries}
              width={width}
              height={height}
              transitionDuration={transitionDuration}
              unit={Unit.ranking}
              getFluentString={getFluentString}
            />
          </>
        );

      } else if (props.type === Type.GrowthProjection) {
        const {growth, rank, rankTotalCount} = props;
        const title = getFluentString('landing-widget-growth-projection-title', {
          year: growthProjectionsYear.toString(),
        });
        const rankText = getFluentString('landing-widget-growth-projection-rank', {
          total: rankTotalCount,
          rank,
        });
        content = (
          <>
            <IndicatorName>{title}</IndicatorName>
            <GrowthProjectionsFigure>{formatGrowthProjection(growth)}</GrowthProjectionsFigure>
            <IndicatorRanking dangerouslySetInnerHTML={{__html: rankText}}/>
          </>
        );

      } else {
        failIfValidOrNonExhaustive(props, 'Invalid widget type ' + JSON.stringify(props));
        content = (<div/>);
      }
    }
  } else {
    content = (<div/>);
  }
  return (
    <Root>
      {content}
    </Root>
  );
};

export default Widget;
